import '@/lib/errorReporter';
import { enableMapSet } from "immer";
enableMapSet();
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import '@/index.css'
import { HomePage } from '@/pages/HomePage'
import { HomePageV2 } from '@/pages/HomePageV2'
import { LoginPage } from '@/pages/LoginPage'
import { VehiclesPage } from '@/pages/VehiclesPage'
import { VehiclesPageV2 } from '@/pages/VehiclesPageV2'
import { VehicleDetailPage } from '@/pages/VehicleDetailPage'
import { AnimalsPage } from '@/pages/AnimalsPage'
import { AnimalsPageV2 } from '@/pages/AnimalsPageV2'
import { AnimalDetailPage } from '@/pages/AnimalDetailPage'
import { PasturesPage } from '@/pages/PasturesPage'
import { PasturesPageV2 } from '@/pages/PasturesPageV2'
import { PastureDetailPage } from '@/pages/PastureDetailPage'
import { InventoryPage } from '@/pages/InventoryPage'
import { InventoryPageV2 } from '@/pages/InventoryPageV2'
import { ContactsPage } from '@/pages/ContactsPage'
import { ContactsPageV2 } from '@/pages/ContactsPageV2'
import { DocumentsPage } from '@/pages/DocumentsPage'
import { DocumentsPageV2 } from '@/pages/DocumentsPageV2'
import { DocumentDetailPage } from '@/pages/DocumentDetailPage'
import { DocumentUploadPage } from '@/pages/DocumentUploadPage'
import { TaxonomyPage } from '@/pages/TaxonomyPage'
import { LogsPage } from '@/pages/LogsPage'
import { LogsPageV2 } from '@/pages/LogsPageV2'
import { ToolsPage } from '@/pages/ToolsPage'
import { ToolsPageV2 } from '@/pages/ToolsPageV2'
import { ToolDetailPage } from '@/pages/ToolDetailPage'
import { GardensPage } from '@/pages/GardensPage'
import { GardensPageV2 } from '@/pages/GardensPageV2'
import { GardenDetailPage } from '@/pages/GardenDetailPage'
import { ImplementsPage } from '@/pages/ImplementsPage'
import { ImplementsPageV2 } from '@/pages/ImplementsPageV2'
import { ImplementDetailPage } from '@/pages/ImplementDetailPage'
import { TemplatesPage } from '@/pages/TemplatesPage'
import { TemplatesPageV2 } from '@/pages/TemplatesPageV2'
import { TemplateDetailPage } from '@/pages/TemplateDetailPage'
import { CropsPage } from '@/pages/CropsPage'
import { CropsPageV2 } from '@/pages/CropsPageV2'
import { CropDetailPage } from '@/pages/CropDetailPage'
import { PlantingsPage } from '@/pages/PlantingsPage'
import { PlantingsPageV2 } from '@/pages/PlantingsPageV2'
import { PlantingDetailPage } from '@/pages/PlantingDetailPage'
import { CropPlannerPage } from '@/pages/CropPlannerPage'
import { SettingsIdentitiesPage } from '@/pages/SettingsIdentitiesPage'
import { SettingsLayout } from '@/pages/SettingsLayout'
import { SettingsProfilePage } from '@/pages/SettingsProfilePage'
import { SsoLinkConfirmPage } from '@/pages/SsoLinkConfirmPage'
import { ComponentsPlaygroundPage } from '@/pages/ComponentsPlaygroundPage'
import { AuthGuardV2 } from '@/components/v2/AuthGuardV2'
import { LayoutPicker } from '@/components/v2/LayoutPicker'

const queryClient = new QueryClient();
const router = createBrowserRouter([
  // Pre-auth routes — no layout chrome.
  { path: "/login", element: <LoginPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/auth/sso/link", element: <SsoLinkConfirmPage />, errorElement: <RouteErrorBoundary /> },

  // Settings route group — sub-sidebar pattern.
  {
    path: "/settings",
    element: (<AuthGuardV2><SettingsLayout /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <SettingsProfilePage /> },
      { path: "profile", element: <SettingsProfilePage /> },
      { path: "identities", element: <SettingsIdentitiesPage /> },
    ],
  },

  // Authenticated content routes.
  {
    path: "/",
    element: (<AuthGuardV2><LayoutPicker v1={<HomePage />} v2={<HomePageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  // /dashboard is a permanent redirect to / — marketing emails, breadcrumbs,
  // and bookmarks all use "Dashboard" as the home label.
  { path: "/dashboard", element: <Navigate to="/" replace />, errorElement: <RouteErrorBoundary /> },
  {
    path: "/dev/components",
    element: (<AuthGuardV2><ComponentsPlaygroundPage /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/vehicles",
    element: (<AuthGuardV2><LayoutPicker v1={<VehiclesPage />} v2={<VehiclesPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  { path: "/vehicles/:id", element: <AuthGuardV2><VehicleDetailPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  {
    path: "/animals",
    element: (<AuthGuardV2><LayoutPicker v1={<AnimalsPage />} v2={<AnimalsPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  { path: "/animals/:id", element: <AuthGuardV2><AnimalDetailPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  {
    path: "/pastures",
    element: (<AuthGuardV2><LayoutPicker v1={<PasturesPage />} v2={<PasturesPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  { path: "/pastures/:id", element: <AuthGuardV2><PastureDetailPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  {
    path: "/inventory",
    element: (<AuthGuardV2><LayoutPicker v1={<InventoryPage />} v2={<InventoryPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  // Phase 1.9 — Operations → Contacts. Backed by the rich /api/contacts
  // surface; both v1 and v2 are wired so users on either layout pick
  // it up immediately.
  {
    path: "/contacts",
    element: (<AuthGuardV2><LayoutPicker v1={<ContactsPage />} v2={<ContactsPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/documents",
    element: (<AuthGuardV2><LayoutPicker v1={<DocumentsPage />} v2={<DocumentsPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  { path: "/documents/upload", element: <AuthGuardV2><DocumentUploadPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  { path: "/documents/:id", element: <AuthGuardV2><DocumentDetailPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  { path: "/taxonomy", element: <AuthGuardV2><TaxonomyPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  {
    path: "/logs",
    element: (<AuthGuardV2><LayoutPicker v1={<LogsPage />} v2={<LogsPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/tools",
    element: (<AuthGuardV2><LayoutPicker v1={<ToolsPage />} v2={<ToolsPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  { path: "/tools/:id", element: <AuthGuardV2><ToolDetailPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  {
    path: "/gardens",
    element: (<AuthGuardV2><LayoutPicker v1={<GardensPage />} v2={<GardensPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  { path: "/gardens/:id", element: <AuthGuardV2><GardenDetailPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  {
    path: "/implements",
    element: (<AuthGuardV2><LayoutPicker v1={<ImplementsPage />} v2={<ImplementsPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  { path: "/implements/:id", element: <AuthGuardV2><ImplementDetailPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  {
    path: "/templates",
    element: (<AuthGuardV2><LayoutPicker v1={<TemplatesPage />} v2={<TemplatesPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  { path: "/templates/:id", element: <AuthGuardV2><TemplateDetailPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  // /crops/planner must come BEFORE /crops/:id so it's matched before the
  // dynamic-segment route catches it.
  { path: "/crops/planner", element: <AuthGuardV2><CropPlannerPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  {
    path: "/crops",
    element: (<AuthGuardV2><LayoutPicker v1={<CropsPage />} v2={<CropsPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  { path: "/crops/:id", element: <AuthGuardV2><CropDetailPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
  {
    path: "/plantings",
    element: (<AuthGuardV2><LayoutPicker v1={<PlantingsPage />} v2={<PlantingsPageV2 />} /></AuthGuardV2>),
    errorElement: <RouteErrorBoundary />,
  },
  { path: "/plantings/:id", element: <AuthGuardV2><PlantingDetailPage /></AuthGuardV2>, errorElement: <RouteErrorBoundary /> },
]);
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <RouterProvider router={router} />
        <Toaster richColors closeButton />
      </ErrorBoundary>
    </QueryClientProvider>
  </StrictMode>,
)
